import { useEffect } from 'react'
import { useIonRouter } from '@ionic/react'
import { FormProvider, useForm } from 'react-hook-form'

import { TextInput } from '../../../components/ui/form/input'
import { useUpdateBroadcast } from '../mutations'
import { composeBroadcastFilters, getBroadcastFormValues } from '../utils'
import { BroadcastBonus } from './broadcast-bonus'
import { BroadcastButtons } from './broadcast-buttons'
import { BroadcastClientFilter } from './broadcast-client-filter'
import { BroadcastEmailField } from './broadcast-email-field'
import { BroadcastMessageType } from './broadcast-message-type'
import { BroadcastRecentlyContacted } from './broadcast-recently-contacted'
import { BroadcastSendFrom } from './broadcast-send-from'
import { BroadcastSmsField } from './broadcast-sms-field'
import { BroadcastTemplateSelect } from './broadcast-template-select'
import { CreditsInfo } from './credits-info'
import { SubscriberCount } from './subscriber-count'

import type { UpdateBroadcastInputs } from '../broadcast.schema'
import type { BroadcastValues } from '../queries'

type Props = {
  admin?: boolean
  backUrl: string
  broadcast: BroadcastValues
}

function EditBroadcastForm({ admin, backUrl, broadcast }: Props) {
  const router = useIonRouter()

  const updateBroadcast = useUpdateBroadcast()

  const form = useForm<UpdateBroadcastInputs>({
    defaultValues: getBroadcastFormValues(broadcast),
  })

  // useImglyUpdate
  useEffect(() => {
    const getValues = form.getValues
    const reset = form.reset

    reset({
      ...getValues(),
      imglyString: broadcast.imglyString,
      setAttachmentsSms: broadcast.attachmentsSms,
    })
  }, [
    broadcast.attachmentsSms,
    broadcast.imglyString,
    form.getValues,
    form.reset,
  ])

  // useUnlayerUpdate
  useEffect(() => {
    const getValues = form.getValues
    const reset = form.reset

    reset({
      ...getValues(),
      emailMessage: broadcast.emailMessage ?? undefined,
    })
  }, [broadcast.emailMessage, form.getValues, form.reset])

  function handleDeleteBroadcast() {
    router.canGoBack() ? router.goBack() : router.push(backUrl, 'back', 'pop')
  }

  function handleSendBroadcast() {
    router.canGoBack() ? router.goBack() : router.push(backUrl, 'back', 'pop')
  }

  const isDisabled = broadcast.status !== 'draft'

  return (
    <div className="ion-padding">
      <FormProvider {...form}>
        <div className="ion-padding-top flex items-center justify-center gap-x-3">
          <SubscriberCount />
          <BroadcastClientFilter
            filters={broadcast.filters}
            isAdmin={admin}
            onSearch={(filters) =>
              updateBroadcast.mutate({
                objectId: broadcast.objectId,
                broadcast: {
                  filter: composeBroadcastFilters({
                    admin,
                    filters,
                  }),
                },
              })
            }
          />
        </div>

        <CreditsInfo broadcastId={broadcast.objectId} />

        {/* Buttons */}
        <BroadcastButtons
          admin={admin}
          broadcastId={broadcast.objectId}
          disabled={isDisabled}
          status={broadcast.status}
          onDelete={handleDeleteBroadcast}
          onSubmit={handleSendBroadcast}
        />

        {/* Title */}
        <div className="py-2">
          <TextInput
            disabled={isDisabled}
            control={form.control}
            name="title"
            label="Title"
          />
        </div>

        {/* Select Template */}
        <BroadcastTemplateSelect disabled={isDisabled} />

        {/* Recently Contacted */}
        <BroadcastRecentlyContacted
          disabled={isDisabled}
          initialState={!broadcast.filters?.notContacted}
          onChange={(notContacted) =>
            updateBroadcast.mutate({
              objectId: broadcast.objectId,
              broadcast: {
                filter: {
                  // should this be getValues('filter')
                  ...composeBroadcastFilters({
                    admin,
                    filters: broadcast.filters,
                  }),
                  notContacted,
                },
              },
            })
          }
        />

        {/* Send From */}
        {admin ? (
          <BroadcastSendFrom />
        ) : (
          <>
            <input type="hidden" {...form.register('fromUserRule')} />
            <input type="hidden" {...form.register('fromUserId')} />
            <input type="hidden" {...form.register('fromName')} />
            <input type="hidden" {...form.register('fromEmailName')} />
            <input type="hidden" {...form.register('fromEmailDomain')} />
          </>
        )}

        {/* Bonus */}
        {admin ? (
          <BroadcastBonus
            broadcastBonus={broadcast.loyaltyBonusRule}
            broadcastId={broadcast.objectId}
            onDelete={() =>
              updateBroadcast.mutate({
                objectId: broadcast.objectId,
                broadcast: { loyaltyBonusRuleId: null },
              })
            }
            onSubmit={(bonusId) => {
              updateBroadcast.mutate({
                objectId: broadcast.objectId,
                broadcast: { loyaltyBonusRuleId: bonusId },
              })
            }}
          />
        ) : null}

        {/* Message Type */}
        <BroadcastMessageType
          disabled={isDisabled}
          onChange={(messageType: UpdateBroadcastInputs['messageType']) =>
            updateBroadcast.mutate({
              objectId: broadcast.objectId,
              broadcast: { messageType },
            })
          }
        />

        {/* SMS */}
        <BroadcastSmsField
          broadcastId={broadcast.objectId}
          disabled={isDisabled}
        />

        {/* Email */}
        <BroadcastEmailField
          broadcastId={broadcast.objectId}
          disabled={isDisabled}
          htmlPreview={broadcast.unlayerImageFull}
          unlayerDesign={broadcast.unlayerDesign}
        />
      </FormProvider>
    </div>
  )
}

export { EditBroadcastForm }
